/**
 * Word (.docx) ve UYAP (.udf) belgelerinden metin çıkarma
 * -------------------------------------------------------
 * Her ikisi de ZIP arşividir:
 *   .docx → word/document.xml
 *   .udf  → content.xml   (UYAP Doküman Formatı)
 *
 * Yeni bir kütüphane (JSZip / mammoth) eklemeden çözmek için
 * tarayıcının yerleşik DecompressionStream('deflate-raw') API'si
 * kullanılıyor. ZIP merkezi dizini elle okunuyor.
 *
 * NOT: Eski ikili .doc biçimi (Word 97-2003) ZIP değildir, OLE2'dir;
 * tarayıcıda güvenilir biçimde açılamaz. O tür için kullanıcıya
 * indirmesi söylenir.
 */

const dv = (buf) => new DataView(buf);
const td = new TextDecoder('utf-8');

/** ZIP: merkezi dizin sonu kaydını (EOCD) sondan geriye arar. */
function eocdBul(buf) {
  const v = dv(buf);
  const n = buf.byteLength;
  const enAz = Math.max(0, n - 66000); // yorum alanı en fazla 64KB
  for (let i = n - 22; i >= enAz; i--) {
    if (v.getUint32(i, true) === 0x06054b50) return i;
  }
  return -1;
}

/** ZIP içindeki dosyaların listesini (ad → konum) çıkarır. */
function zipDizin(buf) {
  const eocd = eocdBul(buf);
  if (eocd < 0) return null;
  const v = dv(buf);
  const adet = v.getUint16(eocd + 10, true);
  let p = v.getUint32(eocd + 16, true);

  const kayitlar = new Map();
  for (let i = 0; i < adet; i++) {
    if (v.getUint32(p, true) !== 0x02014b50) break;
    const yontem = v.getUint16(p + 10, true);
    const sikisikBoyut = v.getUint32(p + 20, true);
    const adUz = v.getUint16(p + 28, true);
    const ekUz = v.getUint16(p + 30, true);
    const yorumUz = v.getUint16(p + 32, true);
    const yerelOfset = v.getUint32(p + 42, true);
    const ad = td.decode(new Uint8Array(buf, p + 46, adUz));
    kayitlar.set(ad, { yontem, sikisikBoyut, yerelOfset });
    p += 46 + adUz + ekUz + yorumUz;
  }
  return kayitlar;
}

/** Belirli bir girdinin içeriğini açar (stored veya deflate). */
async function zipOku(buf, kayit) {
  const v = dv(buf);
  const o = kayit.yerelOfset;
  if (v.getUint32(o, true) !== 0x04034b50) return null;
  const adUz = v.getUint16(o + 26, true);
  const ekUz = v.getUint16(o + 28, true);
  const veriBas = o + 30 + adUz + ekUz;
  const dilim = buf.slice(veriBas, veriBas + kayit.sikisikBoyut);

  if (kayit.yontem === 0) return td.decode(new Uint8Array(dilim)); // sıkıştırılmamış
  if (kayit.yontem !== 8) return null;                             // yalnızca deflate

  if (typeof DecompressionStream === 'undefined') return null;
  const ds = new DecompressionStream('deflate-raw');
  const stream = new Blob([dilim]).stream().pipeThrough(ds);
  const acilmis = await new Response(stream).arrayBuffer();
  return td.decode(new Uint8Array(acilmis));
}

const xmlKacisCoz = (s) =>
  String(s)
    .replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"').replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, d) => String.fromCharCode(Number(d)))
    .replace(/&amp;/g, '&');

/** DOCX: word/document.xml içindeki paragrafları metne çevirir. */
function docxMetin(xml) {
  const paragraflar = xml.split(/<w:p[ >]/).slice(1);
  const satirlar = paragraflar.map((p) => {
    // Satır sonları ve sekmeler
    let s = p.replace(/<w:br\s*\/>/g, '\n').replace(/<w:tab\s*\/>/g, '\t');
    const parcalar = [...s.matchAll(/<w:t(?:\s[^>]*)?>([\s\S]*?)<\/w:t>/g)].map((m) => m[1]);
    return xmlKacisCoz(parcalar.join(''));
  });
  return satirlar.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

/** UDF: content.xml içindeki CDATA gövdesini alır. */
function udfMetin(xml) {
  const cdata = xml.match(/<content>\s*<!\[CDATA\[([\s\S]*?)\]\]>\s*<\/content>/);
  if (cdata) return cdata[1].replace(/\r\n/g, '\n').trim();
  const duz = xml.match(/<content>([\s\S]*?)<\/content>/);
  if (duz) return xmlKacisCoz(duz[1]).trim();
  return '';
}

/**
 * Blob'tan metin çıkarır.
 * @returns {Promise<{tur:string, metin:string, hata?:string}>}
 *   tur: 'docx' | 'udf' | 'metin' | 'desteklenmiyor'
 */
export async function belgeMetniCikar(blob, dosyaAdi = '') {
  const uzanti = String(dosyaAdi).split('.').pop().toLowerCase();

  // Düz metin türleri doğrudan okunur
  if (['txt', 'csv', 'json', 'xml', 'html', 'htm', 'md'].includes(uzanti)) {
    return { tur: 'metin', metin: (await blob.text()).trim() };
  }

  if (uzanti === 'doc') {
    return {
      tur: 'desteklenmiyor',
      metin: '',
      hata: 'Eski Word biçimi (.doc) tarayıcıda açılamıyor. İndirip Word ile açabilirsiniz.',
    };
  }

  if (!['docx', 'udf'].includes(uzanti)) {
    return { tur: 'desteklenmiyor', metin: '', hata: 'Bu dosya türü önizlenemez.' };
  }

  try {
    const buf = await blob.arrayBuffer();
    const dizin = zipDizin(buf);
    if (!dizin) return { tur: 'desteklenmiyor', metin: '', hata: 'Dosya okunamadı (geçerli bir arşiv değil).' };

    if (uzanti === 'docx') {
      const kayit = dizin.get('word/document.xml');
      if (!kayit) return { tur: 'desteklenmiyor', metin: '', hata: 'Word içeriği bulunamadı.' };
      const xml = await zipOku(buf, kayit);
      if (!xml) return { tur: 'desteklenmiyor', metin: '', hata: 'Word içeriği açılamadı.' };
      return { tur: 'docx', metin: docxMetin(xml) };
    }

    // UDF: content.xml (bazı sürümlerde farklı adla)
    const ad = ['content.xml', 'Content.xml'].find((k) => dizin.has(k))
      || [...dizin.keys()].find((k) => k.toLowerCase().endsWith('content.xml'));
    if (!ad) return { tur: 'desteklenmiyor', metin: '', hata: 'UDF içeriği bulunamadı.' };
    const xml = await zipOku(buf, dizin.get(ad));
    if (!xml) return { tur: 'desteklenmiyor', metin: '', hata: 'UDF içeriği açılamadı.' };
    return { tur: 'udf', metin: udfMetin(xml) };
  } catch (e) {
    return { tur: 'desteklenmiyor', metin: '', hata: 'Önizleme çıkarılamadı: ' + (e?.message || '') };
  }
}

/**
 * Metni yazdırma penceresinde açar — kullanıcı "PDF olarak kaydet"i seçer.
 *
 * NEDEN BÖYLE? jsPDF'in gömülü fontları ğ, ş, İ, ı harflerini
 * desteklemiyor ve hukuki bir belgede bu kabul edilemez. Tarayıcının
 * yazdırma hattı sistem fontlarını kullandığı için Türkçe kusursuz çıkar.
 */
/** Yazdırma penceresini açan ortak yardımcı. */
function yazdirmaPenceresiAc(html) {
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(iframe);
  const temizle = () => setTimeout(() => iframe.remove(), 1500);
  iframe.onload = () => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } finally { temizle(); }
  };
  iframe.srcdoc = html;
}

/**
 * Resmi PDF olarak yazdırır (sayfaya sığdırılmış hâlde).
 * Resimlerden metin çıkarılamayacağı için ayrı ele alınır — daha önce
 * bu tür de metin çıkarıcıya gidiyor ve hata veriyordu.
 */
export async function resmiPdfOlarakYazdir(baslik, blob) {
  const dataUrl = await new Promise((cozumle, hata) => {
    const fr = new FileReader();
    fr.onload = () => cozumle(fr.result);
    fr.onerror = hata;
    fr.readAsDataURL(blob);
  });

  const esc = (s) => String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  yazdirmaPenceresiAc(`<!doctype html><html lang="tr"><head><meta charset="utf-8">
<title>${esc(baslik)}</title>
<style>
  @page { size: A4; margin: 1.2cm; }
  html, body { margin:0; padding:0; }
  .sar { display:flex; align-items:center; justify-content:center; min-height:100vh; }
  img { max-width:100%; max-height:100vh; object-fit:contain; }
  @media print { .sar { min-height:auto; } }
</style></head><body>
<div class="sar"><img src="${dataUrl}" alt="${esc(baslik)}"></div>
</body></html>`);
}

export function metniPdfOlarakYazdir(baslik, metin) {
  const esc = (s) => String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const html = `<!doctype html><html lang="tr"><head><meta charset="utf-8">
<title>${esc(baslik)}</title>
<style>
  @page { size: A4; margin: 2cm 1.8cm; }
  body { font-family: "Calibri","Segoe UI",sans-serif; font-size: 11pt; color:#111; line-height:1.55; }
  h1 { font-size: 13pt; margin: 0 0 12pt; color:#2b3652; }
  pre { white-space: pre-wrap; word-wrap: break-word; font-family: inherit; margin:0; }
</style></head><body>
<h1>${esc(baslik)}</h1>
<pre>${esc(metin)}</pre>
</body></html>`;

  yazdirmaPenceresiAc(html);
}
